import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Check, X, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanModal = ({ isOpen, onClose, plan, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    duration: 'monthly',
    bills_limit: '',
    storage_mb: '',
    price: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (plan) {
      // Convert storage_mb to display value (could be in MB or GB format)
      const storageMb = plan.storage_mb || (plan.storage_gb ? plan.storage_gb * 1024 : 512);
      setFormData({
        name: plan.name || '',
        duration: plan.duration || 'monthly',
        bills_limit: plan.bills_limit || '',
        storage_mb: storageMb,
        price: plan.price || '',
      });
    } else {
      setFormData({
        name: '',
        duration: 'monthly',
        bills_limit: '',
        storage_mb: 512, // Default 512 MB
        price: '',
      });
    }
  }, [plan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      name: formData.name,
      duration: formData.duration,
      bills_limit: formData.bills_limit === '' || formData.bills_limit === '0' ? null : parseInt(formData.bills_limit),
      storage_mb: parseFloat(formData.storage_mb),
      price: parseFloat(formData.price),
    };

    await onSave(payload, plan?.id);
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {plan ? 'Edit Plan' : 'Create New Plan'}
            </h3>
            <button
              onClick={onClose}
              data-testid="close-plan-modal-btn"
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Plan Name
              </label>
              <input
                data-testid="plan-name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Duration
              </label>
              <select
                data-testid="plan-duration-select"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Bills Limit (0 for unlimited)
              </label>
              <input
                data-testid="plan-bills-limit-input"
                type="number"
                value={formData.bills_limit}
                onChange={(e) => setFormData({ ...formData, bills_limit: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Storage (MB)
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="plan-storage-input"
                  type="number"
                  value={formData.storage_mb}
                  onChange={(e) => setFormData({ ...formData, storage_mb: e.target.value })}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  required
                  min="100"
                  step="100"
                />
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, storage_mb: 512 })}
                    className={`px-3 py-2 text-xs rounded-lg border ${formData.storage_mb == 512 ? 'bg-violet-100 border-violet-300 text-violet-700' : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                    512MB
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, storage_mb: 1024 })}
                    className={`px-3 py-2 text-xs rounded-lg border ${formData.storage_mb == 1024 ? 'bg-violet-100 border-violet-300 text-violet-700' : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                    1GB
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, storage_mb: 2048 })}
                    className={`px-3 py-2 text-xs rounded-lg border ${formData.storage_mb == 2048 ? 'bg-violet-100 border-violet-300 text-violet-700' : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                    2GB
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, storage_mb: 5120 })}
                    className={`px-3 py-2 text-xs rounded-lg border ${formData.storage_mb == 5120 ? 'bg-violet-100 border-violet-300 text-violet-700' : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                    5GB
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {formData.storage_mb >= 1024 
                  ? `= ${(formData.storage_mb / 1024).toFixed(1)} GB`
                  : `${formData.storage_mb} MB`}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Price (₹ per month)
              </label>
              <input
                data-testid="plan-price-input"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                required
                min="0"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="submit-plan-btn"
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planModal, setPlanModal] = useState({ isOpen: false, plan: null });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/admin/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async (planData, planId) => {
    try {
      if (planId) {
        await axios.put(`${API}/admin/plans/${planId}`, planData);
      } else {
        await axios.post(`${API}/admin/plans`, planData);
      }
      setPlanModal({ isOpen: false, plan: null });
      fetchPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Failed to save plan. Please try again.');
    }
  };

  const handleDeletePlan = async (planId, planName) => {
    if (!window.confirm(`Are you sure you want to delete "${planName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/plans/${planId}`);
      fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Failed to delete plan. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="subscription-plans" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Subscription Plans
          </h1>
          <p className="text-slate-600">{plans.length} plans available</p>
        </div>
        <button
          data-testid="add-plan-btn"
          onClick={() => setPlanModal({ isOpen: true, plan: null })}
          className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-3 rounded-full font-medium hover:shadow-lg transition-all"
        >
          <Plus size={20} />
          Create Plan
        </button>
      </div>

      {/* Discount Info */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Tag className="text-orange-600 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-orange-900 mb-1">Subscription Discounts</h3>
            <p className="text-sm text-orange-800">
              <strong>3 months:</strong> 5% off • <strong>6 months:</strong> 10% off • <strong>1 year:</strong> 15% off
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            data-testid={`plan-card-${index}`}
            className="bg-white rounded-xl border-2 border-slate-100 hover:border-violet-200 p-6 transition-all hover:shadow-lg"
          >
            <div className="mb-4">
              <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {plan.name}
              </h3>
              <p className="text-sm text-slate-500 capitalize">{plan.duration}</p>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-violet-600">₹{plan.price}</span>
                <span className="text-slate-500">/{plan.duration === 'monthly' ? 'mo' : 'yr'}</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>
                  {plan.bills_limit ? `${plan.bills_limit} bills/month` : 'Unlimited bills'}
                </span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>
                  {plan.storage_mb 
                    ? (plan.storage_mb >= 1024 
                        ? `${(plan.storage_mb / 1024).toFixed(plan.storage_mb % 1024 === 0 ? 0 : 1)} GB storage`
                        : `${plan.storage_mb} MB storage`)
                    : (plan.storage_gb ? `${plan.storage_gb} GB storage` : '512 MB storage')}
                </span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>GST compliant</span>
              </li>
            </ul>

            <div className="flex gap-2 pt-4 border-t border-slate-100">
              <button
                data-testid={`edit-plan-${index}-btn`}
                onClick={() => setPlanModal({ isOpen: true, plan })}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Edit2 size={16} />
                Edit
              </button>
              <button
                data-testid={`delete-plan-${index}-btn`}
                onClick={() => handleDeletePlan(plan.id, plan.name)}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No subscription plans found
        </div>
      )}

      <PlanModal
        isOpen={planModal.isOpen}
        onClose={() => setPlanModal({ isOpen: false, plan: null })}
        plan={planModal.plan}
        onSave={handleSavePlan}
      />
    </div>
  );
};

export default SubscriptionPlans;
